import { NoteTypes } from "../../notes/types/notes.types";

export const TriggerSyncActions = {
    Created: "created",
    Updated: "updated",
    Deleted: "deleted",
} as const;

export interface ISSOLaunchResponse {
    landingUrl: string;
    token: string;
}

export const LaunchSSOLandingScreens = {
    Patient: "patient",
    Manage_Medications: "manage_medications",
    Manage_Allergies: "manage_allergies",
    Report: "report",
    Message: "message",
} as const;

export type SSOLandingScreens =
    typeof LaunchSSOLandingScreens[keyof typeof LaunchSSOLandingScreens];

export const LabRequestStatuses = {
    DRAFT: "draft",
    SIGNED: "signed",
} as const;

export type LabRequestStatus =
    typeof LabRequestStatuses[keyof typeof LabRequestStatuses];

export interface ISSOLaunchPayload {
    session_history_id?: string;
    landing_screen?: SSOLandingScreens;
    client_id?: string;
    allow_login_without_patient_context?: boolean;
    prescription_ids?: string[];
}

export interface ITriggerClientSyncPayload {
    session_history_id: string;
    provider_id?: string;
    encounter_date?: string; // MM/DD/YYYY HH:MM:SS format
    action_taken?: typeof TriggerSyncActions[keyof typeof TriggerSyncActions];
}

export interface ITriggerClientSyncResponse {
    patient_identifier: string;
    patient_synced: boolean;
    problem_synced: boolean;
}

export interface ISyncClientClinicalDataResponse {
    allergies: IClientAllergy[];
    allergyLastUpdateDate: string | null;
    medications: IClientMedication[];
    medicationLastUpdateDate: string | null;
}

export interface IClientSyncStatusResponse {
    client_id: string;
    patient_identifier: string;
    is_ready_for_prescribing: boolean;
}

export interface IClientAllergy {
    allergy_id: string;
    allergy_name: string;
    allergy_code: string | null;
    code_system: string | null;
    reaction: string | null;
    severity: string | null;
    onset_date: string | null;
    status: string;
    source: string;
    external_id: string | null;
    created_at: string;
    updated_at: string;
}

export interface IClientMedication {
    medication_id: string;
    medication_name: string;
    drug_code: string | null;
    code_system: string | null;
    dosage: string | null;
    frequency: string | null;
    route: string | null;
    prescriber: string | null;
    start_date: string | null;
    end_date: string | null;
    status: string;
    source: string;
    external_id: string | null;
    created_at: string;
    updated_at: string;
}

export interface IClientAllergyListFilters {
    session_history_id?: string;
}

export interface IClientMedicationsListFilters {
    session_history_id?: string;
}

export interface ISyncPrescriptionDataPayload {
    provider_id?: string;
    note_id?: string;
    session_history_id?: string;
}

export interface ISyncPrescriptionDataResponse {
    prescriptions: IPrescription[];
    prescriptionLastUpdateDate: string;
    prescriptionEvents: IPrescriptionEvent[];
    prescriptionEventsLastUpdateDate: string;
}

export interface IPrescriptionEvent {
    prescription_event_id: string;
    drfirst_prescription_id: string;
    event_type: string;
    event_code: string;
    event_date: string | null;
    prescription_status: PrescriptionStatus | null;
    error_message: string | null;
    from_notification_id: string | null;
    source: string;
    event_hash: string;
    created_at: string;
    updated_at: string;
}

export const PrescriptionEventStatues = {
    PENDING: "pending",
    SENT: "sent",
    SENT_NEEDS_SIGNATURE: "sent_needs_signature",
    SENT_SIGNED: "sent_signed",
    COMPLETED_NEEDS_SIGNATURE: "completed_needs_signature",
    COMPLETED_SIGNED: "completed_signed",
    NEEDS_REVIEW: "needs_review",
    VOIDED: "voided",
} as const;

export type PrescriptionStatus =
    typeof PrescriptionEventStatues[keyof typeof PrescriptionEventStatues];

export interface IPrescription {
    prescription_id: string;
    client_id: string;
    practice_id: string;
    provider_id: string | null;
    note_id: string | null;
    session_history_id: string | null;
    drfirst_prescription_id: string;
    drug_description: string | null;
    quantity: string | null;
    quantity_unit: string | null;
    refills: string | null;
    pharmacy_name: string | null;
    created_date: string | null;
    completed_date: string | null;
    signed_date: string | null;
    last_modified_date: string | null;
    status: PrescriptionStatus;
    is_deleted: boolean;
    source: string;
    created_at: string;
    updated_at: string;
    prescription_events: IPrescriptionEvent[];
    client: {
        client_id: string;
        first_name: string;
        last_name: string;
    };
    provider?: {
        provider_id: string;
        first_name: string;
        last_name: string;
    };
    note?: {
        note_id: string;
        note_type: NoteTypes;
    };
}

export interface IPrescriptionListFilters {
    client_id?: string;
    search_string?: string;
    status?: string;
    source?: string;
    provider_id?: string;
    note_id?: string;
    session_history_id?: string;
    page?: number;
    limit?: number;
    created_date_from?: string | null;
    created_date_to?: string | null;
    completed_date_from?: string | null;
    completed_date_to?: string | null;
    signed_date_from?: string | null;
    signed_date_to?: string | null;
    quantity_from?: string;
    quantity_to?: string;
    last_modified_date_from?: string;
    last_modified_date_to?: string;
}

export interface IClientPrescriptionEventListFilters {
    drfirst_prescription_id?: string;
    event_type?: string;
    event_code?: string;
    prescription_status?: string;
    source?: string;
}

export const PrescriptionEventStatusDescriptions: Record<string, string> = {
    EDQ: "Queued for electronic delivery. An electronic transmission is pending or in transit.",
    EDNR: "Electronic delivery not responded. A prescription will attain this event if a verified status has not been received within 24 hours.",
    EDNV: "Electronic delivery completed, not verified. A 'status' status was received for the transmission.",
    EDV: "Electronic delivery completed, verified.",
    EDF: "Failed electronic delivery. An 'error' was received for the transmission.",
    EFR: "Electronic fill received. Code appears when an RxFill message was sent for a prescription.",
    FDQ: "Queued for delivery by fax. The prescription was sent for fax backup but has not yet been delivered. This status may be repeated when a prescription is re-queued.",
    FDV: "Fax delivery completed. The prescription was faxed successfully. This is a terminal status. Partners must stop polling for a prescription once this status is received.",
    FDF: "Failed fax delivery. The fax attempt failed.",
    UM: "Marked undeliverable. DrFirst staff could not fax the prescription and marked it as undeliverable.",
    UA: "Acknowledged undeliverable. Practice staff acknowledged notice of the undeliverable prescription.",

    CEDQ: "Cancellation queued for electronic delivery. An electronic transmission is pending or in transit.",
    CEDNR: "Cancellation electronic delivery not received. A prescription will attain this event if a verified status has not been received within 12 hours.",
    CEDNV: "Cancellation electronic delivery completed, not verified. A 'status' status was received for the transmission.",
    CEDV: "Cancellation electronic delivery completed, verified. A 'verified' status was received for the transmission.",
    CEDF: "Cancellation failed electronic delivery. An 'error' was received for the transmission.",
    CFDQ: "Queued for delivery by fax. The cancellation was sent for fax backup but has not yet been delivered. This status may be repeated when a cancellation request is re-queued.",
    CFDV: "Fax delivery completed. The cancellation was faxed successfully.",
    CFDF: "Cancellation failed fax delivery. The fax attempt failed.",
    "CUM/CUN":
        "Cancellation marked undeliverable. DrFirst staff could not fax the cancellation and marked it as undeliverable.",
    CUA: "Cancellation acknowledged undeliverable. Practice staff acknowledged notice of the undeliverable cancellation.",

    XEDQ: "Rxchange response queued for electronic delivery. An electronic transmission is pending or in transit.",
    XEDV: "Rxchange response electronic delivery completed, verified. A 'verified' status was received for the transmission.",
    XEDF: "Rxchange response failed electronic delivery. An 'error' was received for the transmission.",
    XEDNR: "Rxchange response electronic delivery not received. A prescription will attain this event if a verified status has not been received within 12 hours.",
    XEDNV: "Rxchange response electronic delivery completed, not verified. A 'status' status was received for the transmission.",
    XFDQ: "Queued for delivery by fax. The Rxchange response was sent for fax backup but has not yet been delivered. This status may be repeated when an Rxchange response is re-queued.",
    XFDV: "Fax delivery completed. The Rxchange response was faxed successfully.",
    XFDF: "Rxchange response failed fax delivery. The fax attempt failed.",
    XUM: "Rxchange response marked undeliverable. DrFirst staff could not fax the Rxchange response and marked it as undeliverable.",
    XUA: "Rxchange response acknowledged undeliverable. Practice staff acknowledged notice of the undeliverable Rxchange response.",
};

export interface IPrescriptionNotificationCount {
    refill: number;
    rx_pending: number;
    rx_need_signing: number;
    rxchange: number;
    rx_orders_pending: number;
}

export interface IDrFirstNotificationCountData {
    notification_counts: IPrescriptionNotificationCount & {
        therapy_request: number;
        drfirst_rx_pending: number;
    };
    fetched_at: string;
    is_stale: boolean;
}

export interface ILabRequestTest {
    lab_request_test_id: string;
    lab_company_test_id: string | null;
    lab_test_id: string;
    lab_category_id: string;
    test_name: string;
    display_name: string;
    short_name: string | null;
    vendor_test_name: string | null;
    vendor_test_code: string | null;
    loinc_code: string | null;
    specimen_type: string | null;
    display_order: number;
}

export interface ILabRequest {
    lab_request_id: string;
    lab_company_id: string | null;
    practice_id: string;
    client_id: string;
    client: {
        first_name: string;
        last_name: string;
        client_id: string;
        email: string | null;
    };
    provider_id: string;
    note_id: string | null;
    diagnosis_codes: string[];
    comment: string | null;
    signature: string | null;
    status: LabRequestStatus;
    created_at: string;
    updated_at: string | null;
    created_by: ILabRequestActor;
    updated_by: ILabRequestActor | null;
    signed_at: string | null;
    signed_by: ILabRequestActor | null;
    tests: ILabRequestTest[];
}

export interface ILabRequestMutationResponse {
    lab_request_id: string;
    lab_company_id: string | null;
    practice_id: string;
    client_id: string;
    provider_id: string;
    note_id: string | null;
    diagnosis_codes: string[];
    comment: string | null;
    signature: string | null;
    status: LabRequestStatus;
    created_at: string;
    updated_at: string | null;
    created_by: string;
    updated_by: string | null;
    signed_at: string | null;
    signed_by: string | null;
    tests: ILabRequestTest[];
}

export interface ILabRequestActor {
    first_name: string;
    last_name: string;
    provider_id: string | null;
}

export interface ILabCompany {
    lab_company_id: string;
    practice_id: string;
    name: string;
    created_at: string;
    updated_at: string | null;
}

export interface ILabCategory {
    lab_category_id: string;
    practice_id: string;
    name: string;
    slug: string;
    sort_order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface ILabTest {
    lab_test_id: string;
    practice_id: string;
    lab_category_id: string;
    name: string;
    display_name: string;
    short_name: string | null;
    description: string | null;
    loinc_code: string | null;
    specimen_type: string | null;
    sort_order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface ILabCompanyTest {
    lab_company_test_id: string;
    lab_company_id: string;
    practice_id: string;
    lab_test_id: string;
    vendor_test_code: string;
    vendor_test_name: string;
    sort_order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface ILabCategoryListFilters {
    is_active?: boolean;
    search_string?: string;
}

export interface ILabTestListFilters {
    lab_category_id?: string;
    is_active?: boolean;
}

export interface ILabCompanyTestListFilters {
    lab_company_id?: string;
    lab_test_id?: string;
    lab_category_id?: string;
    is_active?: boolean;
}

export interface ILabRequestListFilters {
    lab_request_id?: string;
    lab_company_id?: string;
    client_id?: string;
    provider_id?: string;
    note_id?: string;
    status?: string;
    search_string?: string;
    created_date_from?: string | null;
    created_date_to?: string | null;
    page?: number;
    limit?: number;
    sort_by?: "created_at" | "updated_at" | "signed_at" | "status";
    sort_direction?: "asc" | "desc";
}

export interface ICreateDraftLabRequestPayload {
    lab_company_id: string | null;
    client_id: string;
    provider_id: string;
    note_id: string | null;
    selected_lab_test_ids: string[];
}

export interface ICreateLabRequestForm {
    lab_company_id: string | null;
    client_id: string;
    provider_id: string;
    note_id: string | null;
    selected_lab_test_ids: string[];
}

export interface IEditDraftLabRequestPayload {
    lab_company_id: string | null;
    selected_lab_test_ids: string[];
}

export interface ISignLabRequestPayload {
    diagnosis_codes?: string[];
    comment?: string | null;
    signature: string;
}

export interface ISignLabRequestForm {
    diagnosis_codes: string[];
    comment: string;
    signature: string;
}

export interface ISendLabRequestPayload {
    email: string;
}
